import { ActionBarOffsetProvider } from "$components/action-bar/action-bar-offset";
import { LoadingPage } from "$components/loading/loading-page";
import { useInitialiseSentry } from "$routes/_lib/sentry";
import { jssPreset, StylesProvider } from "@material-ui/core";
import { create, type Plugin } from "jss";
import jssStringTemplate from "jss-plugin-template";
import { Suspense } from "react";
import { Outlet } from "react-router-dom";
import { AnalyticsProvider } from "./analytics-provider/provider";
import { ApolloProvider } from "./apollo-provider/provider";
import { Auth0Provider } from "./auth-provider/provider";
import { BillingProvider } from "./billing-provider/provider";
import { useConfig } from "./config-provider/provider";
import { DevToolsProvider } from "./dev-tools-provider/provider";
import { FeatureFlagsProvider } from "./feature-flags-provider/provider";
import { GlobalAppIssueContextProvider } from "./global-app-issue-overlay/provider";
import { LocationHistoryProvider } from "./location-history/provider";
import { MuiProvider } from "./mui-provider/provider";
import { NetworkStatusProvider } from "./network-status-provider/provider";
import { NotificationsProvider } from "./notifications-provider";
import { RxDBNotificationsAdapters } from "./rxdb-provider/notifications";
import { RxDBProvider } from "./rxdb-provider/provider";
import { ServerTimeProvider } from "./server-time-provider/provider";
import { ServiceWorkerProvider } from "./service-worker-provider/provider";
import { TabLeadershipProvider } from "./tab-leadership/provider";
import { TabLeadershipGate } from "./tab-leadership/tab-leadership-gate";
import { Toaster } from "./toast-provider";
import { TranslationsProvider } from "./translations-provider/provider";
import { UserProvider } from "./user-provider";

// Everything in here should eventually be moved to the global-providers according to our standardised structure
// https://elephant.slab.com/posts/rfc-standardised-webapp-project-structure-5mvj31ci
export const GlobalProviders = () => {
  useInitialiseSentry();
  const { appOutOfDatePollRateSeconds } = useConfig();
  return (
    <DevToolsProvider>
      <ServiceWorkerProvider
        updateIntervalMs={appOutOfDatePollRateSeconds * 1000}
      >
        <ServerTimeProvider>
          <StylesProvider
            jss={create({
              plugins: [
                ...jssPreset().plugins,
                jssStringTemplate(),
              ] as Plugin[],
            })}
          >
            <MuiProvider>
              <Auth0Provider>
                <UserProvider>
                  <FeatureFlagsProvider>
                    <ApolloProvider>
                      <AnalyticsProvider>
                        <NetworkStatusProvider>
                          <TranslationsProvider>
                            <GlobalAppIssueContextProvider>
                              <ActionBarOffsetProvider>
                                <Suspense fallback={<LoadingPage />}>
                                  <BillingProvider>
                                    <LocationHistoryProvider>
                                      <TabLeadershipProvider>
                                        <TabLeadershipGate>
                                          <RxDBProvider>
                                            <NotificationsProvider>
                                              <RxDBNotificationsAdapters />
                                              <Outlet />
                                            </NotificationsProvider>
                                          </RxDBProvider>
                                        </TabLeadershipGate>
                                      </TabLeadershipProvider>
                                    </LocationHistoryProvider>
                                  </BillingProvider>
                                </Suspense>
                              </ActionBarOffsetProvider>
                            </GlobalAppIssueContextProvider>
                            <Toaster
                              position="top-right"
                              containerStyle={{ top: "120px" }}
                              toastOptions={{ duration: 5000 }}
                            />
                          </TranslationsProvider>
                        </NetworkStatusProvider>
                      </AnalyticsProvider>
                    </ApolloProvider>
                  </FeatureFlagsProvider>
                </UserProvider>
              </Auth0Provider>
            </MuiProvider>
          </StylesProvider>
        </ServerTimeProvider>
      </ServiceWorkerProvider>
    </DevToolsProvider>
  );
};
